import { motion, AnimatePresence } from 'framer-motion'
import { ChevronRightIcon } from 'lucide-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { cn } from 'src/shared/utils/cn'

import AppPreviewImage from '../../features/home/assets/app-preview-image.png'
import { ProfilePicIcon } from '../../features/home/assets/pfp'
import { ArrowRightLong } from '../icons/arrow-right-long'
import { CircleQuestionIcon } from '../icons/circle-question'
import { KnowledgeCenterIcon } from '../icons/knowledge-center'
import { ThinXIcon } from '../icons/toasts/thin-x-icon'

const presets = {
  signIn: {
    icon: <ProfilePicIcon size={20} className="text-primary" />,
    label: 'Sign in',
  },
  knowledgeCenter: {
    icon: <KnowledgeCenterIcon size={19} className="text-primary" />,
    label: 'Knowledge center',
  },
  helpCenter: {
    icon: <CircleQuestionIcon className="text-primary" />,
    label: 'Help center',
  },
}

export interface IconNavItem {
  /** Assigns default values to label and icon. */
  preset?: 'signIn' | 'knowledgeCenter' | 'helpCenter'
  label?: string
  icon?: React.ReactNode
  onClick?: () => void
  href?: string
}

function IconNavItemView({ item }: { item: IconNavItem }) {
  const icon = item.icon ?? (item.preset ? presets[item.preset].icon : undefined)
  const label = item.label ?? (item.preset ? presets[item.preset].label : 'Button')

  return (
    <div className="text-left text-[20px] font-semibold leading-[1.4] text-primary">
      <a
        href={item.href}
        target="_blank"
        rel="noreferrer noopener"
        className={cn(
          'flex w-full cursor-pointer items-center gap-[11px] focus-visible:outline-none',
          'text-[14px] font-medium leading-[1.4] text-primary',
        )}
        onClick={item.onClick}
      >
        {icon && icon}
        {label && <span>{label}</span>}
      </a>
    </div>
  )
}

interface NavItem {
  label?: string
  onClick?: () => void
  href?: string
  /** Opens the sub navigation with the given title. Used with nestedNavItems. */
  onClickSubNavTitle?: string
  /**
   * Replaces the default nav item with a custom component.
   * Note: All nav items props will be ignored if this is provided.
   */
  componentOverride?: React.ReactNode
}

function NavItemView({ item, openSubNav }: { item: NavItem; openSubNav: (title: string) => void }) {
  if (item.componentOverride) {
    return item.componentOverride
  }

  const handleOnClick = () => {
    if (item.onClickSubNavTitle) {
      openSubNav(item.onClickSubNavTitle)
    }
    item.onClick?.()
  }

  return (
    <div className="text-left text-[20px] font-semibold leading-[1.4] text-primary">
      <a
        href={item.href}
        target="_blank"
        rel="noreferrer noopener"
        onClick={handleOnClick}
        className="block w-full cursor-pointer focus-visible:outline-none"
      >
        {item.label || 'Button'}
      </a>
    </div>
  )
}

interface MobileSidebarProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  openDirection?: 'from-left' | 'from-right'
  /** Main navigation items title. */
  title?: string
  /** Main navigation items shown on the top. */
  mainNavItems?: NavItem[]
  /** Nested navigation items that replaces the main navigation items. */
  nestedNavItems?: {
    title: string
    items: NavItem[]
  }[]
  /** Sub navigation items shown below the main navigation items. */
  iconNavItems?: IconNavItem[]
  showDownloadAppFooter?: boolean
}

const defaultIconNavItems: IconNavItem[] = [
  {
    preset: 'signIn',
  },
  {
    preset: 'knowledgeCenter',
  },
  {
    preset: 'helpCenter',
  },
]

export default function MobileSidebar({
  isOpen,
  setIsOpen,
  openDirection = 'from-right',
  title = 'For homeowners',
  mainNavItems,
  nestedNavItems,
  showDownloadAppFooter = false,
  iconNavItems = defaultIconNavItems,
}: MobileSidebarProps) {
  const navigate = useNavigate()
  const sidebarRef = useRef<HTMLDivElement>(null)
  const dragResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [currentSubNavTitle, setCurrentSubNavTitle] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStartX, setDragStartX] = useState(0)
  const [dragStartY, setDragStartY] = useState(0)
  const [dragCurrentX, setDragCurrentX] = useState(0)
  const [hasDragIntent, setHasDragIntent] = useState(false) // Prevents accidental scroll

  const clearDragResetTimeout = () => {
    if (dragResetTimeoutRef.current) {
      clearTimeout(dragResetTimeoutRef.current)
      dragResetTimeoutRef.current = null
    }
  }

  const resetInlineStyles = () => {
    if (sidebarRef.current) {
      sidebarRef.current.style.transform = ''
      sidebarRef.current.style.transition = ''
    }
  }

  const resetDragState = () => {
    setIsDragging(false)
    setDragStartX(0)
    setDragStartY(0)
    setDragCurrentX(0)
    setHasDragIntent(false)
  }

  const close = () => {
    clearDragResetTimeout()
    resetInlineStyles()
    setIsOpen(false)
    setCurrentSubNavTitle(null)
    resetDragState()
  }

  const closeFromDrag = () => {
    clearDragResetTimeout()
    setIsOpen(false)
    setCurrentSubNavTitle(null)
    resetDragState()
  }

  const { isSubNavOpen, currentNavItems, currentTitle } = useMemo(() => {
    const currentNav = nestedNavItems?.find((item) => item.title === currentSubNavTitle)
    return {
      isSubNavOpen: !!currentSubNavTitle,
      currentNavItems: currentNav?.items ?? mainNavItems,
      currentTitle: currentNav?.title ?? title,
    }
  }, [currentSubNavTitle, mainNavItems, nestedNavItems, title])

  useEffect(() => {
    if (isOpen) {
      clearDragResetTimeout()
      resetInlineStyles()
    }
    return () => {
      clearDragResetTimeout()
    }
  }, [isOpen])

  /**
   * Starts a drag gesture. We track both axes so we can decide whether the user
   * intends a horizontal swipe (close) or vertical scroll.
   */
  const handleDragStart = (clientX: number, clientY: number) => {
    if (!isOpen) {
      return
    }

    clearDragResetTimeout()
    setIsDragging(true)
    setDragStartX(clientX)
    setDragStartY(clientY)
    setDragCurrentX(clientX)
    setHasDragIntent(false)
  }

  /**
   * Moves the sidebar with the pointer once a horizontal intent is detected.
   * The sidebar only follows the drag in the closing direction.
   */
  const handleDragMove = (clientX: number, clientY: number) => {
    if (!isDragging || !sidebarRef.current || !isOpen) {
      return
    }

    let deltaXRaw = clientX - dragStartX
    const deltaY = clientY - dragStartY
    const absDeltaX = Math.abs(deltaXRaw)
    const absDeltaY = Math.abs(deltaY)

    if (!hasDragIntent) {
      if (absDeltaX < 8 && absDeltaY < 8) {
        return
      }

      if (absDeltaY > absDeltaX) {
        return
      }

      // Reset the drag start x so that the drag starts from the current position.
      setHasDragIntent(true)
      setDragStartX(clientX)
      deltaXRaw = 0
    }

    const direction = openDirection === 'from-right' ? 1 : -1
    const dragDistance = deltaXRaw * direction

    if (dragDistance <= 0) {
      return
    }

    setDragCurrentX(clientX)
    sidebarRef.current.style.transform = `translateX(${dragDistance * direction}px)`
    sidebarRef.current.style.transition = 'none'
  }

  /**
   * Ends a drag gesture. If the drag passes the threshold, close the sidebar.
   * Otherwise, snap back and clear inline styles after the transition.
   */
  const handleDragEnd = (clientX?: number) => {
    if (!isDragging || !sidebarRef.current || !isOpen) {
      return
    }

    const direction = openDirection === 'from-right' ? 1 : -1
    const effectiveX = typeof clientX === 'number' ? clientX : dragCurrentX
    const dragDistance = (effectiveX - dragStartX) * direction
    const threshold = 120

    if (dragDistance > threshold) {
      sidebarRef.current.style.transition = 'transform 300ms ease-out'
      sidebarRef.current.style.transform = `translateX(${direction * 100}%)`
      closeFromDrag()
      return
    }

    sidebarRef.current.style.transition = 'transform 300ms ease-out'
    sidebarRef.current.style.transform = 'translateX(0)'
    clearDragResetTimeout()
    dragResetTimeoutRef.current = setTimeout(() => {
      resetInlineStyles()
    }, 300)
    resetDragState()
  }

  // Touch event handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    handleDragStart(e.touches[0].clientX, e.touches[0].clientY)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    handleDragMove(e.touches[0].clientX, e.touches[0].clientY)
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touch = e.changedTouches[0]
    handleDragEnd(touch?.clientX)
  }

  // Mouse event handlers (for desktop)
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) {
      return
    }

    handleDragStart(e.clientX, e.clientY)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    handleDragMove(e.clientX, e.clientY)
  }

  const handleMouseUp = (e: React.MouseEvent) => {
    handleDragEnd(e.clientX)
  }

  const handleMouseLeave = (e: React.MouseEvent) => {
    if (isDragging) {
      handleDragEnd(e.clientX)
    }
  }

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-[51] bg-black/30 transition-opacity duration-300',
          isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={close}
      />
      <aside
        ref={sidebarRef}
        className={cn(
          'fixed top-0 z-[100] flex h-lvh w-full flex-col bg-white transition-transform duration-300 sm:w-[400px]',
          isOpen ? 'translate-x-0' : openDirection === 'from-left' ? '-translate-x-full' : 'translate-x-full',
          openDirection === 'from-left' ? 'left-0' : 'right-0',
        )}
        style={{ boxShadow: '2px 0 16px 0 rgba(0,0,0,0.10)' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <div className="flex flex-row-reverse items-center justify-between pl-[17px] pr-3 pt-4 xs:pt-5">
          {/* Close Button */}
          <div className="flex min-h-6 justify-end">
            <button onClick={close} className="flex items-center justify-center p-2" aria-label="Close menu">
              <ThinXIcon className="text-primary" size={15} />
            </button>
          </div>

          {/* Back Button */}
          <div className={cn('flex min-h-6 justify-start', !isSubNavOpen && 'hidden')}>
            <button
              onClick={() => setCurrentSubNavTitle(null)}
              className="flex items-center justify-center p-2"
              aria-label="Back"
            >
              <ArrowRightLong className="rotate-180 text-primary" size={21} />
            </button>
          </div>
        </div>

        {/* Navigation Content */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex flex-col gap-10 px-[25px] pb-[32px] pt-[52px]">
            {/* Main Navigation Section */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentTitle}
                initial={{ opacity: 0, x: isSubNavOpen ? 20 : -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: isSubNavOpen ? 20 : -20 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                className="flex flex-col gap-4"
              >
                <p className="text-[14px] font-semibold leading-[1.4] text-muted">{currentTitle}</p>
                <nav className="flex flex-col gap-5">
                  {currentNavItems?.map((item, index) => (
                    <NavItemView key={`nav-item-${index}`} item={item} openSubNav={setCurrentSubNavTitle} />
                  ))}
                </nav>
              </motion.div>
            </AnimatePresence>

            {/* Sub Navigation Section */}
            <div className="flex flex-col gap-5">
              {iconNavItems?.map((item, index) => (
                <IconNavItemView key={`sub-nav-item-${index}`} item={item} />
              ))}
            </div>
          </div>
        </div>

        {/* Download App Footer */}
        <button
          onClick={() => {
            navigate(paths.downloadApp.getHref())
          }}
          className={cn(
            'mt-auto flex w-full cursor-pointer items-center overflow-hidden rounded-t-[40px] bg-[#f8f7f5]',
            !showDownloadAppFooter && 'hidden',
          )}
          style={{ height: 150 }}
        >
          {/* App Screenshot */}
          <div className="relative h-full w-1/2 min-w-[140px] shrink-0">
            <img
              src={AppPreviewImage}
              alt="Conrad App Preview"
              className="absolute inset-0 object-contain pl-4 pt-[25px]"
            />
          </div>

          {/* Text Content */}
          <div className="flex flex-1 flex-col items-start justify-center pl-4 pr-1 text-left">
            <p className="text-[12px] font-medium leading-[1.4] text-muted">The Conrad App</p>
            <div className="mt-[2px] flex items-center gap-2">
              <p className="text-[16px] font-semibold leading-[1.4] text-primary">Available in the App Store</p>
              {/* Chevron Arrow */}
              <div className="mr-5 shrink-0">
                <ChevronRightIcon size={24} className="text-primary" />
              </div>
            </div>
          </div>
        </button>
      </aside>
    </>
  )
}
